import { useState, type ChangeEvent } from 'react'
import { Eye, EyeOff, MailWarning } from 'lucide-react'
import { useSnackbar } from '@shared/ui/notification-context'
import { useTranslation } from 'react-i18next'
import {
  changeCurrentUserPassword,
  getCurrentFirebaseUser,
  IncorrectCurrentPasswordError,
  sendPasswordResetEmailToUser,
  type DashboardUserProfile,
} from '@services/firebase'
import { Card, CardAction, CardContent, CardHeader, CardTitle, Field, FieldLabel, LmsButton, LmsInput } from '@shared/ui'
import '../profile-layout.css'

type PasswordFieldKey = 'current' | 'next' | 'confirm'

export function ChangePasswordSettingsCard({
  profile,
  shell = 'card',
}: {
  readonly profile: DashboardUserProfile
  readonly shell?: 'card' | 'plain'
}) {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation('form_account')
  const { t: tCommon } = useTranslation('translation')
  const [form, setForm] = useState({ current: '', next: '', confirm: '' })
  const [visible, setVisible] = useState<Record<PasswordFieldKey, boolean>>({
    current: false,
    next: false,
    confirm: false,
  })
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isSendingReset, setIsSendingReset] = useState(false)

  const hasPasswordProvider =
    getCurrentFirebaseUser()?.providerData.some(
      (provider) => provider?.providerId === 'password',
    ) ?? true

  const handleFieldChange =
    (key: PasswordFieldKey) => (event: ChangeEvent<HTMLInputElement>) => {
      setForm((previous) => ({ ...previous, [key]: event.target.value }))
      setErrorMessage(null)
    }

  const renderPasswordField = ({
    autoComplete,
    fieldKey,
    label,
  }: {
    readonly autoComplete: string
    readonly fieldKey: PasswordFieldKey
    readonly label: string
  }) => {
    const isVisible = visible[fieldKey]
    const inputId = `change-password-${fieldKey}`

    return (
      <Field className="profileFormRow">
        <FieldLabel htmlFor={inputId}>{label}</FieldLabel>
        <div className="profilePasswordField">
          <LmsInput
            autoComplete={autoComplete}
            id={inputId}
            onChange={handleFieldChange(fieldKey)}
            type={isVisible ? 'text' : 'password'}
            value={form[fieldKey]}
          />
          <button
            aria-label={
              isVisible ? t('auth.password.hide') : t('auth.password.show')
            }
            className="profilePasswordToggle"
            onClick={() => {
              setVisible((previous) => ({ ...previous, [fieldKey]: !previous[fieldKey] }))
            }}
            onMouseDown={(event) => {
              event.preventDefault()
            }}
            type="button"
          >
            {isVisible ? (
              <EyeOff aria-hidden="true" size={18} strokeWidth={2.25} />
            ) : (
              <Eye aria-hidden="true" size={18} strokeWidth={2.25} />
            )}
          </button>
        </div>
      </Field>
    )
  }

  const handleSubmit = async () => {
    setErrorMessage(null)

    if (!form.current) {
      setErrorMessage(t('Please enter your current password'))
      return
    }

    if (!form.next) {
      setErrorMessage(t('Please enter a new password'))
      return
    }

    if (form.next.length < 6) {
      setErrorMessage(t('Password must be at least 6 characters'))
      return
    }

    if (form.next === form.current) {
      setErrorMessage(t('New password must be different from current password'))
      return
    }

    if (!form.confirm) {
      setErrorMessage(t('Please confirm your new password'))
      return
    }

    if (form.next !== form.confirm) {
      setErrorMessage(t('Password mismatch'))
      return
    }

    try {
      setIsSaving(true)
      await changeCurrentUserPassword(form.current, form.next)
      enqueueSnackbar(t('Password changed successfully'), { variant: 'success' })
      setForm({ current: '', next: '', confirm: '' })
    } catch (error) {
      if (error instanceof IncorrectCurrentPasswordError) {
        setErrorMessage(t('Current password is incorrect'))
      } else {
        enqueueSnackbar(t('Updated failed'), { variant: 'error' })
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleSendResetEmail = async () => {
    if (!profile.email) {
      return
    }

    try {
      setIsSendingReset(true)
      await sendPasswordResetEmailToUser(profile.email)
      enqueueSnackbar(t('message_reset_password'), { variant: 'success' })
    } catch {
      enqueueSnackbar(t('Updated failed'), { variant: 'error' })
    } finally {
      setIsSendingReset(false)
    }
  }

  const googleOnlyContent = (
    <div className="profileSectionStack">
      <div className="profileSubscriptionNotice is-info" role="note">
        <MailWarning aria-hidden="true" className="profileSubscriptionNoticeIcon" />
        <div>
          <p className="profileSubscriptionNoticeTitle">
            {t('This account signs in with Google')}
          </p>
          <p className="profileSubscriptionNoticeBody">
            {t(
              'Set a password via email to enable email and password sign-in for this account.',
            )}
          </p>
        </div>
      </div>
      <div className="profileActionRow">
        <LmsButton
          isLoading={isSendingReset}
          loadingLabel={t('Submitting', { defaultValue: 'Sending' })}
          onClick={() => void handleSendResetEmail()}
          variant="primary"
        >
          {t('Send password reset email')}
        </LmsButton>
      </div>
    </div>
  )

  const formContent = (
    <>
      <div className="profileFormStack profilePasswordForm">
        {renderPasswordField({
          autoComplete: 'current-password',
          fieldKey: 'current',
          label: t('Current password'),
        })}
        {renderPasswordField({
          autoComplete: 'new-password',
          fieldKey: 'next',
          label: t('New password'),
        })}
        {renderPasswordField({
          autoComplete: 'new-password',
          fieldKey: 'confirm',
          label: t('Confirm new password'),
        })}
        <p className="profileMutedText">
          {t('Password must be at least 6 characters')}
        </p>
      </div>

      {errorMessage ? (
        <p className="profileErrorState" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </>
  )

  const actions = hasPasswordProvider ? (
    <LmsButton
      className="min-h-11 px-6"
      isLoading={isSaving}
      loadingLabel={t('Submitting', { defaultValue: 'Saving' })}
      onClick={() => void handleSubmit()}
      variant="primary"
    >
      {t('Update password')}
    </LmsButton>
  ) : null

  const content = hasPasswordProvider ? formContent : googleOnlyContent

  if (shell === 'plain') {
    return (
      <div className="profileCardStack">
        <div className="profileHeaderRow">
          <h2 className="profileCardTitle">{tCommon('Change Password')}</h2>
          {actions ? <div className="profileHeaderAction">{actions}</div> : null}
        </div>

        {content}

        {actions ? (
          <div className="profileActionRow profileMobileSubmit">{actions}</div>
        ) : null}
      </div>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{tCommon('Change Password')}</CardTitle>
        {actions ? <CardAction>{actions}</CardAction> : null}
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  )
}
